/**
 * NotificationPreferencesPage - Sprint 8/11
 * User notification preferences for SSE events and alerts
 * Added: Send Test Digest button (admin-only)
 * Sprint 11 P0: Email status chip + Test Email endpoint
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Bell, Volume2, AlertTriangle, Sparkles, RefreshCw, Save, Check, Mail, Send, Loader2, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NotificationPreferencesPage = () => {
  const [preferences, setPreferences] = useState({
    sync_events: true,
    errors_only: false,
    ai_suggestions: true,
    demand_updates: true,
    conversion_alerts: true,
    email_digest: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [sendingTestDigest, setSendingTestDigest] = useState(false);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [emailStatus, setEmailStatus] = useState(null);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('simpliview_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }, []);

  const fetchPreferences = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/user/notification-preferences`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        if (data.data) {
          setPreferences(prev => ({ ...prev, ...data.data }));
        }
      }

      // Get user role for admin check
      const profileResponse = await fetch(`${API_URL}/api/auth/me`, {
        headers: getAuthHeaders()
      });
      if (profileResponse.ok) {
        const profileData = await profileResponse.json();
        // Support both legacy role field and new roles array
        const user = profileData.user || profileData;
        let role = user.role;
        if (!role && user.roles && user.roles.length > 0) {
          role = user.roles[0].role_key;
        }
        setUserRole(role);
        
        // Fetch email status if admin (includes sysadmin)
        if (role === 'admin' || role === 'owner' || role === 'sysadmin') {
          const emailResponse = await fetch(`${API_URL}/api/notifications/email/status`, {
            headers: getAuthHeaders()
          });
          if (emailResponse.ok) {
            const emailData = await emailResponse.json();
            setEmailStatus(emailData);
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch preferences:', err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  const handleToggle = (key) => {
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/user/notification-preferences`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(preferences)
      });

      if (response.ok) {
        toast.success('Preferences saved');
        setSaved(true);
        // Update localStorage for SSE listener
        localStorage.setItem('notificationPreferences', JSON.stringify(preferences));
      } else {
        toast.error('Failed to save preferences');
      }
    } catch (err) {
      console.error('Save failed:', err);
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleSendTestDigest = async () => {
    setSendingTestDigest(true);
    try {
      const response = await fetch(`${API_URL}/api/notifications/digest/test`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        if (data.mode === 'mock') {
          toast.success('Test digest sent (mock mode) - Check server logs');
        } else {
          toast.success('Test digest sent successfully to your email');
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send test digest');
      }
    } catch (err) {
      console.error('Send test digest failed:', err);
      toast.error('Failed to send test digest');
    } finally {
      setSendingTestDigest(false);
    }
  };

  const handleSendTestEmail = async () => {
    setSendingTestEmail(true);
    try {
      const response = await fetch(`${API_URL}/api/notifications/test-email`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        if (data.mode === 'mock') {
          toast.success('Test email logged (mock mode) - No SendGrid key configured');
        } else {
          toast.success(`Test email sent to ${data.recipient}`);
        }
        // Refresh status
        const emailResponse = await fetch(`${API_URL}/api/notifications/email/status`, {
          headers: getAuthHeaders()
        });
        if (emailResponse.ok) {
          setEmailStatus(await emailResponse.json());
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send test email');
      }
    } catch (err) {
      console.error('Send test email failed:', err);
      toast.error('Failed to send test email');
    } finally {
      setSendingTestEmail(false);
    }
  };

  const isAdmin = userRole === 'admin' || userRole === 'sysadmin' || userRole === 'owner';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl mx-auto" data-testid="notification-preferences">
      <div className="mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Bell className="h-6 w-6" />
          Notification Preferences
        </h1>
        <p className="text-gray-500 mt-1">
          Customize how you receive alerts and updates
        </p>
      </div>

      {/* Real-Time Notifications */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Volume2 className="h-5 w-5 text-blue-500" />
            Real-Time Notifications
          </CardTitle>
          <CardDescription>
            Control which events trigger toast notifications
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="font-medium">Sync Events</p>
              <p className="text-sm text-gray-500">
                Notifications for ADO/Jira sync activities
              </p>
            </div>
            <Switch
              checked={preferences.sync_events}
              onCheckedChange={() => handleToggle('sync_events')}
              data-testid="toggle-sync-events"
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="font-medium flex items-center gap-2">
                Errors Only
                <Badge variant="outline" className="text-xs">Filter</Badge>
              </p>
              <p className="text-sm text-gray-500">
                Only show error notifications, suppress success messages
              </p>
            </div>
            <Switch
              checked={preferences.errors_only}
              onCheckedChange={() => handleToggle('errors_only')}
              data-testid="toggle-errors-only"
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="font-medium">Demand Updates</p>
              <p className="text-sm text-gray-500">
                Notifications for demand transitions and approvals
              </p>
            </div>
            <Switch
              checked={preferences.demand_updates}
              onCheckedChange={() => handleToggle('demand_updates')}
              data-testid="toggle-demand-updates"
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="font-medium">Conversion Alerts</p>
              <p className="text-sm text-gray-500">
                Notifications when demands are converted to projects
              </p>
            </div>
            <Switch
              checked={preferences.conversion_alerts}
              onCheckedChange={() => handleToggle('conversion_alerts')}
              data-testid="toggle-conversion-alerts"
            />
          </div>
        </CardContent>
      </Card>

      {/* AI Notifications */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-500" />
            AI Assistant Notifications
          </CardTitle>
          <CardDescription>
            Control AI-related alerts and suggestions
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="font-medium">AI Suggestions Alerts</p>
              <p className="text-sm text-gray-500">
                Get notified when AI analysis completes
              </p>
            </div>
            <Switch
              checked={preferences.ai_suggestions}
              onCheckedChange={() => handleToggle('ai_suggestions')}
              data-testid="toggle-ai-suggestions"
            />
          </div>
        </CardContent>
      </Card>

      {/* Email Preferences */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Mail className="h-5 w-5 text-orange-500" />
            Email Notifications
          </CardTitle>
          <CardDescription>
            Configure email digest preferences
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="font-medium">Daily Email Digest</p>
              <p className="text-sm text-gray-500">
                Receive a daily summary of all notifications
              </p>
            </div>
            <Switch
              checked={preferences.email_digest}
              onCheckedChange={() => handleToggle('email_digest')}
              data-testid="toggle-email-digest"
            />
          </div>

          {/* Admin-only: Send Test Digest */}
          {isAdmin && (
            <div className="pt-4 border-t">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="font-medium flex items-center gap-2">
                    Send Test Digest
                    <Badge variant="outline" className="text-xs">Admin</Badge>
                  </p>
                  <p className="text-sm text-gray-500">
                    Send a test digest email to verify configuration
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSendTestDigest}
                  disabled={sendingTestDigest}
                  data-testid="send-test-digest"
                >
                  {sendingTestDigest ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Send Test
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Admin-only: Email Service Status */}
          {isAdmin && emailStatus && (
            <div className="pt-4 border-t">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="font-medium flex items-center gap-2">
                    Email Service Status
                    <Badge 
                      variant={emailStatus.mode === 'live' ? 'default' : 'secondary'}
                      className={emailStatus.mode === 'live' ? 'bg-green-600' : 'bg-gray-500'}
                    >
                      {emailStatus.mode === 'live' ? (
                        <><CheckCircle className="h-3 w-3 mr-1" /> Active (SendGrid)</>
                      ) : (
                        <><XCircle className="h-3 w-3 mr-1" /> Mock Mode</>
                      )}
                    </Badge>
                  </p>
                  <p className="text-sm text-gray-500">
                    {emailStatus.mode === 'live' 
                      ? `Sending from ${emailStatus.from_email}` 
                      : 'Configure SENDGRID_API_KEY to enable'}
                  </p>
                  {emailStatus.stats_7d && (
                    <p className="text-xs text-gray-400 mt-1">
                      Last 7 days: {emailStatus.stats_7d.sent} sent, {emailStatus.stats_7d.mocked} mocked, {emailStatus.stats_7d.failed} failed
                    </p>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSendTestEmail}
                  disabled={sendingTestEmail}
                  data-testid="send-test-email"
                >
                  {sendingTestEmail ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="h-4 w-4 mr-2" />
                      Test Email
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Admin-only: Email Templates Link */}
      {isAdmin && (
        <Card className="mt-6">
          <CardHeader className="py-4">
            <CardTitle className="text-sm">Email Templates</CardTitle>
            <CardDescription>
              Customize email templates for lifecycle stages
            </CardDescription>
          </CardHeader>
          <CardContent className="py-3">
            <Button
              variant="outline"
              onClick={() => window.location.href = '/settings/notifications/templates'}
              data-testid="manage-templates-btn"
            >
              <Mail className="h-4 w-4 mr-2" />
              Manage Templates
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Save Button */}
      <div className="flex justify-end gap-3">
        <Button
          variant="outline"
          onClick={fetchPreferences}
          disabled={saving}
        >
          Reset
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          data-testid="save-preferences"
          className={saved ? 'bg-green-600 hover:bg-green-700' : ''}
        >
          {saving ? (
            <>
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : saved ? (
            <>
              <Check className="h-4 w-4 mr-2" />
              Saved
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Preferences
            </>
          )}
        </Button>
      </div>

      {/* Info Card */}
      <Card className="mt-6 bg-blue-50 border-blue-200">
        <CardContent className="py-4">
          <p className="text-sm text-blue-700">
            <strong>Note:</strong> Changes take effect immediately for real-time notifications. 
            Email preferences may take up to 24 hours to update.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default NotificationPreferencesPage;
